import { useEffect, useState } from 'react'

import { useToast } from '../../context/ToastContext'
import {
  type ComicCharacterCard,
  createEmptyCharacterCard,
  normalizeCharacterCards,
} from '../../lib/comicCharacters'
import {
  canSetCharacterReferenceUrl,
  type ComicPageReferenceCapContext,
  getPageReferenceSlotUsage,
  pageReferenceCapExceededMessage,
} from '../../lib/comicPageReferenceCap'
import { ChromeToolbarDangerButton } from '../ChromeActions'
import { ChromeComposerTextarea } from '../ChromeComposerTextarea'
import { ChromeReferenceImagePicker } from '../ChromeReferenceImagePicker'
import { chromeFieldBaseClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { HorizontalChipStrip } from '../HorizontalChipStrip'
import {
  ComicCharacterCardAddTile,
  ComicCharacterCardPreviewTile,
} from './ComicCharacterCardPreviewTile'
import { comicPanelRoundSm } from './comicPanelTokens'

type Props = {
  angular: boolean
  value: ComicCharacterCard[]
  onChange: (next: ComicCharacterCard[]) => void
  /** 当前页分镜上下文：用于校验造型参考是否超过模型 cap */
  referenceCap?: ComicPageReferenceCapContext | null
}

export function ComicCharacterCardsEditor({
  angular,
  value,
  onChange,
  referenceCap = null,
}: Props) {
  const { showToast } = useToast()
  const cards = normalizeCharacterCards(value)
  const [selectedId, setSelectedId] = useState<string | null>(cards[0]?.id ?? null)

  useEffect(() => {
    if (cards.length === 0) {
      setSelectedId(null)
      return
    }
    if (!selectedId || !cards.some((c) => c.id === selectedId)) {
      setSelectedId(cards[0]!.id)
    }
  }, [cards, selectedId])

  const selected = cards.find((c) => c.id === selectedId) ?? null

  const usage =
    referenceCap && referenceCap.cap > 0
      ? getPageReferenceSlotUsage(
          referenceCap.creative,
          referenceCap.pagePanels,
          referenceCap.cap,
        )
      : null

  const patchCard = (id: string, partial: Partial<ComicCharacterCard>) => {
    onChange(
      normalizeCharacterCards(
        cards.map((c) => (c.id === id ? { ...c, ...partial } : c)),
      ),
    )
  }

  const removeCard = (id: string) => {
    const next = cards.filter((c) => c.id !== id)
    onChange(next)
    if (selectedId === id) {
      setSelectedId(next[0]?.id ?? null)
    }
  }

  const addCard = () => {
    const card = createEmptyCharacterCard(`角色 ${cards.length + 1}`)
    onChange([...cards, card])
    setSelectedId(card.id)
  }

  return (
    <div className="space-y-4">
      <HorizontalChipStrip
        className="pb-1"
        trackClassName="items-stretch gap-3"
        role="list"
        aria-label="角色卡列表"
      >
        {cards.map((card) => (
          <ComicCharacterCardPreviewTile
            key={card.id}
            angular={angular}
            card={card}
            selected={card.id === selectedId}
            onClick={() => setSelectedId(card.id)}
          />
        ))}
        <ComicCharacterCardAddTile angular={angular} onClick={addCard} />
      </HorizontalChipStrip>

      {selected ? (
        <div
          className={[
            'space-y-3 border border-line bg-panel/40 p-4',
            comicPanelRoundSm(angular),
          ].join(' ')}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-ink">编辑：{selected.name.trim() || '未命名'}</p>
            <ChromeTooltip
              content={cards.length <= 1 ? '至少保留一张角色卡' : '删除该角色'}
              angular={angular}
              side="top"
              align="end"
            >
              <span className="inline-flex shrink-0">
                <ChromeToolbarDangerButton
                  type="button"
                  className="h-8 shrink-0 px-2.5 text-xs"
                  onClick={() => removeCard(selected.id)}
                  disabled={cards.length <= 1}
                >
                  删除
                </ChromeToolbarDangerButton>
              </span>
            </ChromeTooltip>
          </div>

          <label className="block text-sm font-semibold text-ink">
            角色名
            <input
              type="text"
              value={selected.name}
              maxLength={40}
              onChange={(e) => patchCard(selected.id, { name: e.target.value })}
              placeholder="例如：林迟"
              className={[
                chromeFieldBaseClass(angular),
                'mt-1.5 block w-full px-3 py-2 text-sm text-ink outline-none',
              ].join(' ')}
            />
          </label>

          <label className="block text-sm font-semibold text-ink">
            外貌 / 服装
            <ChromeComposerTextarea
              angular={angular}
              variant="form"
              value={selected.appearance}
              onChange={(e) => patchCard(selected.id, { appearance: e.target.value })}
              placeholder="发型、服装、体型等，便于跨格保持一致"
              maxLength={600}
              textareaClassName="min-h-[3.5rem]"
            />
          </label>

          <div>
            <p className="mb-1.5 text-sm font-semibold text-ink">造型参考图</p>
            <ChromeReferenceImagePicker
              angular={angular}
              mode="single"
              urls={selected.referenceImageUrl ? [selected.referenceImageUrl] : []}
              onChange={(urls) => {
                const nextUrl = urls[0] ?? null
                if (referenceCap) {
                  const check = canSetCharacterReferenceUrl(
                    referenceCap.creative,
                    referenceCap.pagePanels,
                    selected.id,
                    nextUrl,
                    referenceCap.cap,
                  )
                  if (!check.ok) {
                    showToast({ message: check.message, variant: 'warning' })
                    return
                  }
                }
                patchCard(selected.id, { referenceImageUrl: nextUrl })
              }}
              disabled={
                Boolean(
                  referenceCap &&
                    referenceCap.cap > 0 &&
                    usage &&
                    usage.remaining <= 0 &&
                    !selected.referenceImageUrl?.trim(),
                )
              }
              hint={
                referenceCap && referenceCap.cap > 0
                  ? usage && usage.remaining > 0
                    ? `生图时与本页场景参考合并发送；本页最多 ${referenceCap.cap} 张，还可占 ${usage.remaining} 张。`
                    : `本页参考图已满（上限 ${referenceCap.cap} 张）。该角色若在本页出场，请先去掉其它参考图。`
                  : referenceCap && referenceCap.cap === 0
                    ? '当前模型不支持参考图。'
                    : '生图时优先使用该角色的参考图（需模型支持图生图）'
              }
              compact
              emptyPlaceholder="点击从图库选择造型图"
              pickerTitle="选择造型参考图"
              atLimitMessage={
                referenceCap && referenceCap.cap > 0
                  ? pageReferenceCapExceededMessage(referenceCap.cap)
                  : undefined
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}
